import { BufferGeometry, type LineBasicMaterial } from 'three';
import { markRaw } from 'vue';
import { lineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import type { CurveAngleRefs, LandmarkMap } from '@/domain/anatomy/hipSpineAngleOverlays';
import type { AngleNodes, HipSpineAngleResult } from '@/views/3d-planner/hipSpine/types';
import { resolveCurveGeometry, toWorld } from '@/views/3d-planner/hipSpine/angles/angleGeometry';
import { ARC_SEGMENTS, tessellateBezier } from '@/domain/geometry/tessellate';
import { prepareLabelPlacement } from '@/views/3d-planner/hipSpine/angles/angleLabels';

export type CurveBuildContext = {
    groupId: string;
    landmarkMap: LandmarkMap;
    lineMaterial: LineBasicMaterial;
    sphereRadius: number;
    colour: string;
};

export function buildCurveAngleNodes(
    angle: HipSpineAngleResult,
    refs: CurveAngleRefs,
    ctx: CurveBuildContext,
): AngleNodes | null {
    const geo = resolveCurveGeometry(refs, ctx.landmarkMap);
    if (!geo) return null;

    const children: StandardNode[] = [];

    const bezVerts = tessellateBezier(
        toWorld(geo.bezierStart),
        toWorld(geo.bezierControl),
        toWorld(geo.bezierEnd),
        ARC_SEGMENTS,
    );
    if (bezVerts.length >= 2) {
        children.push(
            lineSegmentsNode(`bezier-${angle.id}`, {
                parent: ctx.groupId,
                name: `bezier-${angle.id}`,
                geometry: markRaw(new BufferGeometry().setFromPoints(bezVerts)),
                material: ctx.lineMaterial,
            }),
        );
    }

    children.push(
        lineSegmentsNode(`ep1-${angle.id}`, {
            parent: ctx.groupId,
            name: `ep1-${angle.id}`,
            geometry: markRaw(
                new BufferGeometry().setFromPoints([toWorld(geo.ep1Start), toWorld(geo.ep1End)]),
            ),
            material: ctx.lineMaterial,
        }),
    );

    children.push(
        lineSegmentsNode(`ep2-${angle.id}`, {
            parent: ctx.groupId,
            name: `ep2-${angle.id}`,
            geometry: markRaw(
                new BufferGeometry().setFromPoints([toWorld(geo.ep2Start), toWorld(geo.ep2End)]),
            ),
            material: ctx.lineMaterial,
        }),
    );

    const labelPlacement = prepareLabelPlacement({
        angleId: angle.id,
        groupId: ctx.groupId,
        position: geo.labelPos,
        value: angle.value,
        colour: ctx.colour,
        sphereRadius: ctx.sphereRadius,
    });

    return { children, labelPlacement };
}
